/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-bitwise */
import { locale } from '@ps/web-poker-dates';
import { TournFlagsServInt } from '@ps/web-poker-types';

import type { Language } from '../types';
import { TournamentState } from '../types';

export enum MT_STATUS {
  MT_STATUS_PUBLISHED = 0,
  // #define	MT_STATUS_REG_START			1	// registration started
  // #define	MT_STATUS_REG_END			2	// registration closed
  SEATS_AVAIL = 1, // users might take their seats
  TOURN_STARTED = 2, // tournament started
  TOURN_CLOSED = 3, // tournament closed
  TOURN_ALREADY_CLOSED = 4, // used only in crash recovery
  TOURN_CANCELLED = 5, // tourney is cancelled for some reason
  TOURN_CANCELLED_ADMIN = 6, // tourney is cancelled for some reason

  MT_STATUS_MASK = 7 // all but registration bits
}

export enum MT_STATUS_BIT {
  UNREGISTRATION_OPEN = 1 << 8,
  REGISTRATION_OPEN = 1 << 7,
  REGISTRATION_CLOSED = 1 << 9,
  UNREGISTRATION_CLOSED = 1 << 10,
  LIMIT_REACHED = 1 << 11,
  REBUY_OPEN = 1 << 12,
  ADDON_OPEN = 1 << 13,
  HUBRACKET_AVAILABLE = 1 << 14,
  BLITZ_MODE = 1 << 15,
  REGISTRATION_PAUSED = 1 << 16,
  BUBBLE_REACHED = 1 << 17, // PYR-71953
  REENTRY_OPEN = 1 << 18 // PYR-107729
}

export enum TestStateValues {
  Announced = MT_STATUS.MT_STATUS_PUBLISHED,
  Registering = MT_STATUS.MT_STATUS_PUBLISHED | MT_STATUS_BIT.REGISTRATION_OPEN,
  TournamentAnnouncedFull = MT_STATUS.MT_STATUS_PUBLISHED | MT_STATUS_BIT.REGISTRATION_CLOSED,
  Seating = MT_STATUS.SEATS_AVAIL,
  TournamentSeatingFull = MT_STATUS.SEATS_AVAIL | MT_STATUS_BIT.REGISTRATION_CLOSED,
  Running = MT_STATUS.TOURN_STARTED,
  LateRegistration = MT_STATUS.TOURN_STARTED | MT_STATUS_BIT.REGISTRATION_OPEN,
  PausedRegistration = MT_STATUS.TOURN_STARTED | MT_STATUS_BIT.REGISTRATION_PAUSED,
  Cancelled = MT_STATUS.TOURN_CANCELLED,
  Completed = MT_STATUS.TOURN_CLOSED
}

export const isRegOpen = (status?: number) => {
  if (status === undefined) return false;

  const maskedStatus = status & MT_STATUS.MT_STATUS_MASK;
  const isOpenStatus = maskedStatus < MT_STATUS.TOURN_CLOSED;

  const isOpenBits = (status & MT_STATUS_BIT.REGISTRATION_OPEN) === MT_STATUS_BIT.REGISTRATION_OPEN;

  return isOpenStatus && isOpenBits;
};

export const isRunning = (status: number) => {
  if (status === undefined) return false;
  const maskedStatus = status & MT_STATUS.MT_STATUS_MASK;
  return Boolean(maskedStatus === MT_STATUS.TOURN_STARTED);
};

export const isCancelled = (status: number) =>
  [MT_STATUS.TOURN_CANCELLED, MT_STATUS.TOURN_CANCELLED_ADMIN].includes(
    status & MT_STATUS.MT_STATUS_MASK
  );

export const getMttState = (
  status?: number,
  isRestricted?: number | boolean,
  tournFlagsServInt?: number
) => {
  if (status === undefined) return undefined;

  const maskedStatus = status & MT_STATUS.MT_STATUS_MASK;
  const registrationOpen = status & MT_STATUS_BIT.REGISTRATION_OPEN;
  const registrationClosed = status & MT_STATUS_BIT.REGISTRATION_CLOSED;
  const registrationPaused = status & MT_STATUS_BIT.REGISTRATION_PAUSED;
  const isFlightTarget =
    tournFlagsServInt &&
    (tournFlagsServInt & TournFlagsServInt.TOURNFLAG_SERVINT_TARGET_FLIGHT) ===
      TournFlagsServInt.TOURNFLAG_SERVINT_TARGET_FLIGHT;
  // @TODO: implement analog logic to the following one for usePhased -> const auto usePhased( defined( _FLIGHTS_USE_WORD_PHASED_ ) );
  const usePhased = true;

  switch (maskedStatus) {
    case MT_STATUS.MT_STATUS_PUBLISHED:
      if (isFlightTarget) {
        return usePhased ? TournamentState.PHASE : TournamentState.FLIGHTED;
      }
      if (isRestricted) {
        return TournamentState.RESTRICTED;
      }
      if (registrationOpen) {
        return TournamentState.REGISTERING;
      }
      if (registrationClosed) {
        return TournamentState.FULL;
      }
      return TournamentState.ANNOUNCED;

    case MT_STATUS.SEATS_AVAIL:
      if (registrationClosed && isFlightTarget) {
        return usePhased ? TournamentState.PHASE : TournamentState.FLIGHTED;
      }
      if (registrationClosed) {
        return TournamentState.FULL;
      }
      return TournamentState.SEATING;

    case MT_STATUS.TOURN_STARTED:
      if (registrationPaused) {
        return TournamentState.PAUSED;
      }
      if (registrationOpen && isFlightTarget) {
        return usePhased ? TournamentState.PHASE : TournamentState.FLIGHTED;
      }
      if (registrationOpen) {
        return TournamentState.LATE_REGISTRATION;
      }
      return TournamentState.RUNNING;

    case MT_STATUS.TOURN_CLOSED:
      return TournamentState.COMPLETED;

    case MT_STATUS.TOURN_CANCELLED:
    case MT_STATUS.TOURN_CANCELLED_ADMIN:
      return TournamentState.CANCELLED;

    default:
      return undefined;
  }
};

const {
  bg,
  cs,
  da,
  de,
  el,
  enGB,
  enIN,
  enUS,
  es,
  et,
  fi,
  fr,
  hr,
  hu,
  it,
  ja,
  ko,
  lt,
  lv,
  nl,
  nn,
  pl,
  pt,
  ptBR,
  ro,
  ru,
  sk,
  sl,
  sv,
  uk,
  zhCN,
  zhTW
} = locale;

export const Locales: Record<Language, Locale> = {
  en: enUS,
  'en-GB': enGB,
  'en-US': enUS,
  'en-IN': enIN,
  cs,
  da,
  de,
  el,
  es,
  'es-419': es,
  et,
  fr,
  hr,
  it,
  lv,
  lt,
  hu,
  nl,
  no: nn,
  pl,
  pt,
  'pt-br': ptBR,
  ro,
  sl,
  sk,
  sv,
  fi,
  bg,
  ru,
  uk,
  ja,
  'zh-cn': zhCN,
  'zh-tw': zhTW,
  ko,
  cz: cs
};
